import { CartStore } from "assets/cart";
import { InitBundle } from "sections/_bundle/bundle";
import { CardProductCardCtaButtonBlock, CardProductCardGalleryBlock, CardProductCardLabelsBlock, CardProductCardPriceBlock, CardProductCardVariantSelectorColorBlock } from "types/blocks";
import { CardProductCardSection } from "../../@types/sections";
import { _Product_hydrated, _Variant_hydrated } from "../../assets/types";

const initProductCard = (
  $el: HTMLElement,
  $refs: Record<string, HTMLElement>,
  productHandle: string,
  productId: number,
  variantId?: number,
  default_select_selling_plan = false
) => {
  const random_id = utils.shortUUID();
  const addon_target = $el
    .closest(".shopify-section")
    ?.querySelector(`form[data-handle="${$el.getAttribute("data-addon-product-target")}"]`);
  const addon_auto_add = $el.hasAttribute("data-addon-auto-add");
  const addon_bundle_in_cart = $el.hasAttribute("data-addon-cart-bundle");
  const preorder_threshold = +(window.theme_settings.data__product__variant__preorder_threshold ?? 1);

  const cart = window.Alpine.store("cart") as CartStore;

  const settings = utils.JSONParse<CardProductCardSection["settings"]>($el.getAttribute("data-settings"));

  const gallery = $el.querySelector(`[data-product-card-gallery]`);
  const gallerySettings = utils.JSONParse<CardProductCardGalleryBlock["settings"]>(gallery?.getAttribute("data-settings"));

  const option_blocks = [
    ...($el.querySelectorAll("[data-options-block]")?.length
      ? $el.querySelectorAll("[data-options-block]")
      : $el.querySelector("template")?.content?.querySelectorAll("[data-options-block]") ?? []),
  ].map((block) =>
    utils.JSONParse<{
      block_id: string;
      option_type: "by_name" | "by_index" | "fallback_options";
      match_option_index: number;
      match_option_titles: string;
      match_exact_word: boolean;
    }>(block.getAttribute("data-options-block"))
  );

  let product = _products[productHandle] ?? _product.getHtmlProduct(productHandle);

  if (!product) {
    _product.getProductData(productHandle, productId).then((res) => {
      updateProductState(res);
    });
  }
  product ??= _products[productHandle];

  if (settings?.default_variant_metafield) {
    const [namespace, key] = settings.default_variant_metafield.trim().split(".");
    const variant = product?.metafields?.[namespace]?.[key] as _Product_hydrated["variants"][number];
    variantId = variant?.id;
  }

  const selected_variant = _product.getSelectedVariant(product, variantId);

  const selling_plan_allocations = selected_variant?.selling_plan_allocations;
  const selected_selling_plan =
    default_select_selling_plan || product?.requires_selling_plan ? selling_plan_allocations?.[0]?.selling_plan : null;
  const selling_plan_discount_wording = +selected_selling_plan?.price_adjustments?.[0]?.value
    ? selected_selling_plan?.price_adjustments?.[0]?.value_type === "fixed_amount"
      ? `${utils.formatMoney(selected_selling_plan?.price_adjustments?.[0]?.value)}`
      : selected_selling_plan?.price_adjustments?.[0]?.value_type === "percentage"
      ? `${selected_selling_plan?.price_adjustments?.[0]?.value}%`
      : ""
    : "";

  const available_quantity =
    selected_variant?.inventory_management === "shopify" && selected_variant?.inventory_policy === "deny"
      ? selected_variant?.inventory_quantity -
        cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0)
      : selected_variant?.preorder && selected_variant?.preorder_quantity
      ? selected_variant?.inventory_quantity +
        selected_variant?.preorder_quantity -
        cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0)
      : 99999;

  const state = window.Alpine.reactive({
    product_handle: productHandle,
    random_id,
    addon_target,
    addon_auto_add,
    addon_bundle_in_cart,
    hydrated: !!product,
    variant_changed: false,
    settings,
    product: product as _Product_hydrated,
    selected_media_id: product?.media?.[0]?.id,
    properties: {},
    selected_variant,
    selling_plan_allocations,
    selected_selling_plan,
    selling_plan_discount_wording,
    isAdding: false,
    selected_options: selected_variant?.options,
    quantity: 1,
    sibling_handle: "",
    hasVariants: product?.variants?.length > 1 || product?.variants?.[0]?.title !== "Default Title",
    hasSubscription: !!selected_variant?.options?.length,
    soldOut: available_quantity <= 0,
    available_quantity,
    preorder:
      selected_variant?.preorder &&
      selected_variant?.inventory_quantity -
        cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0) <
        preorder_threshold,
    gallery_media: product?.media ?? [],
  });

  const handleImageSelection = (
    filter_images?: CardProductCardGalleryBlock["settings"]["filter_images"],
    variant = state.selected_variant
  ) => {
    switch (filter_images) {
      case "show_all": {
        state.gallery_media = state.product?.media;
        break;
      }
      case "selected_variant": {
        state.gallery_media = state.product?.media?.filter((media) => media.id === variant?.featured_media?.id);
        if (!state.gallery_media?.length) {
          state.gallery_media = [state.product?.featured_media];
        }
        break;
      }
      case "variant_images_by_order": {
        let hide = true;

        state.gallery_media = state.product?.media?.filter((media) => {
          if (media.id === variant?.featured_media?.id) {
            hide = false;
          }

          if (state.product?.variants.map((v) => v?.featured_media?.id).includes(media.id)) {
            hide = true;
          }
          return !hide;
        });

        break;
      }
      case "variant_images_by_metafield": {
        state.gallery_media = [
          ...(state.product?.media?.filter((media) => media.id === variant?.featured_media?.id) ?? []),
          ...(state.selected_variant?.metafields?.smart?.images ?? []),
        ];

        if (!state.gallery_media?.length) {
          state.gallery_media = state.product?.media;
        }

        break;
      }
      case "variant_images_and_unassigned": {
        state.gallery_media = [
          ...(state.product?.media?.filter((media) => media.id === variant?.featured_media?.id) ?? []),
          ...(state.product?.media?.filter(
            (media) => !state.product?.variants.map((v) => v?.featured_media?.id).includes(media.id)
          ) ?? []),
        ];
        break;
      }
      case "only_unassigned": {
        state.gallery_media = state.product?.media?.filter(
          (media) => !state.product?.variants.map((v) => v?.featured_media?.id).includes(media.id)
        );
        break;
      }
      case "first_or_selected_image": {
        state.gallery_media = state.product?.media?.filter((media) => media.id === state.selected_media_id);
        break;
      }
    }
  };

  const scrollToMediaById = async (mediaId: number, scrollContainer?: HTMLElement) => {
    const isHorizontal = () => {
      return !!(
        getComputedStyle(scrollContainer).overflowY === "hidden" ||
        getComputedStyle(scrollContainer).gridAutoFlow === "column" ||
        scrollContainer.scrollWidth > scrollContainer?.clientWidth
      );
    };
    await utils.delay(5);

    const image = scrollContainer?.querySelector<HTMLElement>(`[data-media-id="${mediaId}"]`);

    if (!image) return;
    await utils.delay(5);
    const header = document.querySelector<HTMLElement>(".header-sections-container");
    const offsetHeight = Math.max(180, header?.getBoundingClientRect()?.bottom ?? 0);
    const imageOffset = utils.getElementPosition(image);

    if (utils.isElementScrollable(scrollContainer)) {
      let scrollSnap = false;
      if (["snap-both", "snap-x", "snap-y"].some((c) => scrollContainer?.classList?.contains(c))) {
        scrollContainer?.classList.remove("snap-both", "snap-x", "snap-y");
        scrollSnap = true;
      }
      utils.scrollToXY(200, image.offsetLeft, image.offsetTop, scrollContainer as HTMLElement, () => {
        if (scrollSnap) {
          scrollContainer?.classList.add(isHorizontal() ? "snap-x" : "snap-y");
        }
        state.selected_media_id = mediaId;
      });
      return;
    }

    if (scrollContainer.closest("[data-modal-id]")) return;

    utils.scrollToXY(
      200,
      image.offsetLeft,
      imageOffset.top - offsetHeight - +getComputedStyle(scrollContainer).paddingTop.replace("px", ""),
      window,
      () => {
        state.selected_media_id = mediaId;
      }
    );
  };

  const handleAddToCart = async (e: PointerEvent, openCartDrawer = true) => {
    const properties = {};
    const form = $el.querySelector("form");
    const giftItemBlockId = $el.closest("[data-gift-with-purchase]")?.getAttribute("data-gift-with-purchase");
    const isHiddenItem = !!$el.closest("[data-hide-in-cart]");

    if (giftItemBlockId) {
      properties["_gift_with_purchase"] = giftItemBlockId;
    }
    if (isHiddenItem) {
      properties["_p_hidden"] = "true";
    }

    const smartArmor = state.product?.metafields?.smart?.armor;
    if (smartArmor) {
      properties["_smart_armor"] = JSON.stringify(smartArmor);
    }

    if (state.selected_variant?.preorder && state.selected_variant?.inventory_quantity < preorder_threshold) {
      properties["Preorder"] = `true`;
      if (state.selected_variant?.preorder_date) {
        properties["Preorder"] = `Shipping ${new Date(state.selected_variant.preorder_date).toLocaleDateString(
          navigator.language,
          {
            month: "short",
            year: "numeric",
          }
        )}`;
      }
    }

    if (form) {
      Object.entries(utils.serializeForm($el.querySelector("form")))?.forEach(([key, value]) => {
        if (key.includes("properties[") && value) {
          properties[key.replace(/^properties\[(.*)]$/gi, "$1")] = value;
        }
      });
    }

    e?.preventDefault();
    e?.stopPropagation();
    state.isAdding = true;
    const data = await _cart.add({
      items: [
        {
          id: state.selected_variant.id,
          quantity: state.quantity,
          selling_plan: cart.global_subscriptions
            ? state.selected_variant?.selling_plan_allocations?.find(
                (plan) => plan.selling_plan?.id === cart.cart_selling_plan_id
              )?.selling_plan?.id
            : state.selected_selling_plan?.id,
          properties: {
            ...properties,
            ...state.properties,
          },
        },
      ],
    });
    state.isAdding = false;

    if (openCartDrawer) {
      _stores.modal.setId("modal--cart-drawer");
      _stores.quickView.show = false;
    }
  };

  const handleBackInStockNotification = async (e: PointerEvent) => {
    if (state?.selected_variant?.available && !state?.selected_variant?.preorder) return;
    e.preventDefault();
    e.stopPropagation();
    _stores.modal.setId("back_in_stock");
    _stores.backInStockNotification.product = state.product;
    _stores.backInStockNotification.selected_variant = state.selected_variant;
  };

  const setSelectedVariant = (id: number) => {
    state.selected_variant = state.product.variants?.find((variant) => variant.id === id);
    state.selected_options = state.selected_variant.options;
    state.variant_changed = true;

    if (
      state.selected_selling_plan &&
      !state.selected_variant?.selling_plan_allocations?.some(
        (plan) => plan?.selling_plan?.id === state.selected_selling_plan?.id
      )
    ) {
      state.selected_selling_plan = state.selected_variant?.selling_plan_allocations?.[0]?.selling_plan ?? null;
    }

    if (state.product.requires_selling_plan && !state.selected_selling_plan) {
      state.selected_selling_plan =
        state.selected_variant?.selling_plan_allocations?.[0]?.selling_plan ??
        state.product.selling_plan_groups?.[0]?.selling_plans?.[0];
    }

    if (gallerySettings?.scroll_to_selected_variant_image && state.selected_variant?.featured_media?.id) {
      scrollToMediaById(state.selected_variant?.featured_media?.id);
    }
    _product.lastOptions = {
      ...(_product.lastOptions ?? {}),
      ...(state.product.options_with_values?.reduce((acc, option, index) => {
        acc[option?.name?.toLowerCase()] = state.selected_options[index];
        return acc;
      }, {}) ?? {}),
    };
    setTimeout(() => {
      sessionStorage.setItem("_p_last_options", JSON.stringify(_product.lastOptions));
    });
  };

  const setProductOption = ({ index, value }: { index: number; value: string }) => {
    const options = [...state.selected_options];
    options[index] = value;

    setSelectedVariant(
      state.product.variants?.find((variant) => variant.options.every((option, i) => options[i] === option))?.id ??
        state.product?.variants?.find((variant) => variant.options[index] === value)?.id ??
        state.product?.variants?.find(({ available }) => available)?.id ??
        state.product?.variants?.[0]?.id
    );
  };

  const setSellingPlan = (selling_plan_id: number | undefined) => {
    state.selected_selling_plan = state.selling_plan_allocations.find(
      (allocation) => allocation.selling_plan.id === selling_plan_id
    )?.selling_plan;

    const selling_plan = state.selected_selling_plan ?? selling_plan_allocations?.[0]?.selling_plan;

    state.selling_plan_discount_wording =
      selling_plan?.price_adjustments?.[0]?.value_type === "fixed_amount"
        ? `${utils.formatMoney(selling_plan?.price_adjustments?.[0]?.value)}`
        : selling_plan?.price_adjustments?.[0]?.value_type === "percentage"
        ? `${selling_plan?.price_adjustments?.[0]?.value}%`
        : "";
  };

  const getDynamicText = (content: string) => {
    return utils.getBracketInputDynamicPluralizedText(content, state);
  };

  const getDynamicValue = (content: string) => {
    return utils.getBracketInputDynamicValue(content, state);
  };

  const getDynamicValueWithFallbacks = (content: string) => {
    return content.split(",")?.reduce((acc, item) => {
      acc ||= utils.getBracketInputDynamicValue(item.trim(), state);
      return acc;
    }, "");
  };

  const getContentLabels = (key: string) => {
    const labels = key.split(".").reduce<any>((acc, selector) => {
      if (!selector || acc === undefined || acc === null) return acc || "";

      if (acc && selector in acc) {
        return acc[selector];
      }
      return "";
    }, state);

    if (Array.isArray(labels)) {
      return labels.filter(Boolean);
    }
    return [labels].filter(Boolean);
  };

  const getDiscountLabel = (type: CardProductCardLabelsBlock["settings"]["label_type__discounts"]) => {
    const price = state.selected_variant?.price ?? 0;
    const compare_at_price = state.selected_variant?.compare_at_price ?? 0;

    if (compare_at_price <= price) {
      return "";
    }
    switch (type) {
      case "sale": {
        return "Sale";
      }
      case "percentage": {
        return `${Math.round(((compare_at_price - price) * 100) / compare_at_price)}% off`;
      }
      case "value": {
        return `Save ${utils.formatMoney(
          compare_at_price - price,
          window?.money_format?.replace(/\{(\s*)amount(\s*)}/gi, "{$1amount_no_decimals$2}")
        )}`;
      }
      default: {
        return "";
      }
    }
  };

  const getVariantSwatches = (
    primary_source: CardProductCardVariantSelectorColorBlock["settings"]["primary_source"],
    fallback_source: CardProductCardVariantSelectorColorBlock["settings"]["fallback_source"],
    variant: _Product_hydrated["variants"][number]
  ) => {
    const cssVariables = [];
    switch (primary_source) {
      case "title": {
        cssVariables.push(`--primary-swatch: var(--swatch-${utils.handlelize(variant.title)})`);
        break;
      }
      case "image": {
        const [src, version] = variant?.featured_media?.preview_image?.src.split("?") ?? [];
        if (!src) break;
        cssVariables.push(`--primary-swatch: url(${src}?width=180&height=180&${version}) center center/cover no-repeat`);
        break;
      }
      case "metafield": {
        const color_swatch = variant.metafields.smart.color_swatch;

        if (typeof color_swatch === "string") {
          cssVariables.push(`--primary-swatch: ${color_swatch}`);
          break;
        }
        if (color_swatch) {
          const [src, version] = color_swatch?.preview_image?.src.split("?") ?? [];
          if (!src) break;
          cssVariables.push(`--primary-swatch: url(${src}?width=180&height=180&${version}) center center/cover no-repeat`);
          cssVariables.push(`--primary-swatch: ${color_swatch}`);
        }
        break;
      }
    }
    switch (fallback_source) {
      case "title": {
        cssVariables.push(`--fallback-swatch: var(--swatch-${utils.handlelize(variant.title)})`);
        break;
      }
      case "image": {
        const [src, version] = variant?.featured_media?.preview_image?.src.split("?") ?? [];
        if (!src) break;
        cssVariables.push(`--fallback-swatch: url(${src}?width=180&height=180&${version}) center center/cover no-repeat`);
        break;
      }
      case "metafield": {
        const color_swatch = variant.metafields.smart.color_swatch;

        if (typeof color_swatch === "string") {
          cssVariables.push(`--fallback-swatch: ${color_swatch}`);
          break;
        }
        if (color_swatch) {
          const [src, version] = color_swatch?.preview_image?.src.split("?") ?? [];
          if (!src) break;
          cssVariables.push(`--fallback-swatch: url(${src}?width=180&height=180&${version}) center center/cover no-repeat`);
          cssVariables.push(`--fallback-swatch: ${color_swatch}`);
        }
        break;
      }
    }

    return `${cssVariables?.join(";")};background: var(--primary-swatch, var(--fallback-swatch, ${utils.handlelize(
      variant?.title
    )}))`;
  };

  const getOptionIndex = (blockId) => {
    const optionBlocks: { index: number; id: string }[] = [];

    for (let i = 0; i < (state.product?.options?.length || 0); i++) {
      const option = state.product?.options[i];
      const byNameBlock = option_blocks?.find((block) => {
        const alreadyUsed = optionBlocks.some((b) => b.id === block.block_id);
        if (alreadyUsed || block.option_type !== "by_name") return false;

        if (block.match_exact_word) {
          return block.match_option_titles?.split(",").includes(option);
        }
        return block.match_option_titles
          ?.split(",")
          .map((o) => o.toLowerCase().trim())
          .includes(option.toLowerCase().trim());
      });

      if (byNameBlock) {
        optionBlocks.push({ index: i, id: byNameBlock.block_id });
        continue;
      }

      const byIndexOptions = option_blocks?.find(
        (block) =>
          !optionBlocks.some((b) => b.id === block.block_id) && block.option_type === "by_index" && block.match_option_index === i
      );
      if (byIndexOptions) {
        optionBlocks.push({ index: i, id: byIndexOptions.block_id });
        continue;
      }

      const fallbackOptions = option_blocks?.find(
        (block) =>
          !optionBlocks.some((b) => b.id === block.block_id) &&
          block.option_type === "fallback_options" &&
          block.match_option_index === i
      );
      if (fallbackOptions) {
        optionBlocks.push({ index: i, id: fallbackOptions.block_id });
      }
    }

    return optionBlocks?.find((block) => block.id === blockId)?.index ?? -1;
  };

  const getOptionSwatches = (
    primary_source: CardProductCardVariantSelectorColorBlock["settings"]["primary_source"],
    fallback_source: CardProductCardVariantSelectorColorBlock["settings"]["fallback_source"],
    value: _Product_hydrated["options_with_values"][number]["values"][number],
    index: number
  ) => {
    const variant = state?.product?.variants?.find((variant) => variant.options[index] === value);

    const cssVariables = [];
    switch (primary_source) {
      case "title": {
        cssVariables.push(`--primary-swatch: var(--swatch-${utils.handlelize(value)})`);
        break;
      }
      case "image": {
        const [src, version] = variant?.featured_media?.preview_image?.src?.split("?") ?? [];
        if (!src) break;
        cssVariables.push(`--primary-swatch: url(${src}?width=180&height=180&${version}) center center/cover no-repeat`);
        break;
      }
      case "metafield": {
        const color_swatch = variant.metafields.smart.color_swatch;

        if (typeof color_swatch === "string") {
          cssVariables.push(`--primary-swatch: ${color_swatch}`);
          break;
        }
        if (color_swatch) {
          const [src, version] = color_swatch?.preview_image?.split("?") ?? [];
          if (!src) break;
          cssVariables.push(`--primary-swatch: url(${src}?width=180&height=180&${version}) center center/cover no-repeat`);
        }
        break;
      }
    }

    switch (fallback_source) {
      case "title": {
        cssVariables.push(`--fallback-swatch: var(--swatch-${utils.handlelize(value)})`);
        break;
      }
      case "image": {
        const [src, version] = variant?.featured_media?.preview_image?.src?.split("?") ?? [];
        if (!src) break;
        cssVariables.push(`--fallback-swatch: url(${src}?width=180&height=180&${version}) center center/cover no-repeat`);
        break;
      }
      case "metafield": {
        const color_swatch = variant.metafields.smart.color_swatch;

        if (typeof color_swatch === "string") {
          cssVariables.push(`--fallback-swatch: ${color_swatch}`);
          break;
        }
        if (color_swatch) {
          const [src, version] = color_swatch?.preview_image?.split("?") ?? [];
          if (!src) break;
          cssVariables.push(`--fallback-swatch: url(${src}?width=180&height=180&${version}) center center/cover no-repeat`);
        }
        break;
      }
    }

    return `${cssVariables?.join(";")};background: var(--primary-swatch, var(--fallback-swatch, ${utils.handlelize(value)}))`;
  };

  const truncateChildren = (element: HTMLElement, container: HTMLElement) => {
    const children = [...container.children] as HTMLElement[];
    if (children.at(-2)?.offsetLeft + children.at(-2)?.clientWidth < container.clientWidth) {
      return children.length - 2;
    }
    return (
      children.findIndex(
        (childElement) =>
          childElement.offsetLeft +
            childElement.clientWidth +
            element.clientWidth +
            +getComputedStyle(container).gap.replace("px", "") +
            15 >
          container.clientWidth
      ) - 1
    );
  };

  const hasAvailableVariant = (index: number, value: string) => {
    return state?.product?.variants?.some((variant) => {
      switch (index) {
        case 0: {
          return (
            variant.options[index] === value &&
            variant.options[1] === state?.selected_options[1] &&
            variant.options[2] === state?.selected_options[2] &&
            variant.available
          );
        }
        case 1: {
          return (
            variant.options[0] === state?.selected_options[0] &&
            variant.options[2] === state?.selected_options[2] &&
            variant.options[index] === value &&
            variant.available
          );
        }
        case 2: {
          return (
            variant.options[0] === state?.selected_options[0] &&
            variant.options[1] === state?.selected_options[1] &&
            variant.options[index] === value &&
            variant.available
          );
        }
      }
      return false;
    });
  };

  const updateProductState = (product: _Product_hydrated, selectedVariantId?: number, selectedSellingPlanId?: number) => {
    if (settings?.default_variant_metafield && product) {
      const [namespace, key] = settings.default_variant_metafield.trim().split(".");
      const variant = product?.metafields?.[namespace]?.[key] as _Product_hydrated["variants"][number];
      product.selected_variant_id = variant?.id ?? product?.selected_variant_id;
    }

    const selected_variant = _product.getSelectedVariant(product, selectedVariantId);

    const selling_plan_allocations = selected_variant?.selling_plan_allocations;
    const selected_selling_plan = selectedSellingPlanId
      ? selling_plan_allocations?.find((plan) => plan.selling_plan.id === selectedSellingPlanId)?.selling_plan
      : default_select_selling_plan
      ? selling_plan_allocations?.[0]?.selling_plan
      : null;
    const selling_plan_discount_wording = +selected_selling_plan?.price_adjustments?.[0]?.value
      ? selected_selling_plan?.price_adjustments?.[0]?.value_type === "fixed_amount"
        ? `${utils.formatMoney(selected_selling_plan?.price_adjustments?.[0]?.value)} off`
        : selected_selling_plan?.price_adjustments?.[0]?.value_type === "percentage"
        ? `Save ${selected_selling_plan?.price_adjustments?.[0]?.value}%`
        : ""
      : "";

    state.product = product;
    state.product_handle = product?.handle ?? state.product_handle ?? productHandle;
    state.selected_media_id = product?.media?.[0]?.id;
    state.properties = {};
    state.selected_variant = selected_variant;
    state.selling_plan_allocations = selling_plan_allocations;
    state.selected_selling_plan = selected_selling_plan;
    state.selling_plan_discount_wording = selling_plan_discount_wording;
    state.isAdding = false;
    state.selected_options = selected_variant?.options;
    state.quantity = 1;
    state.sibling_handle = "";
    state.gallery_media = product?.media;
    state.hydrated = true;
  };

  const showConditionally = (
    condition: CardProductCardPriceBlock["settings"]["show_conditionally"],
    bundle?: ReturnType<InitBundle>["bundle"]
  ) => {
    switch (condition) {
      case "always": {
        return true;
      }
      case "with_subscriptions": {
        return !!state.selected_variant?.selling_plan_allocations?.length;
      }
      case "no_subscriptions": {
        return !state.selected_variant?.selling_plan_allocations?.length;
      }
      case "no_bundle": {
        return !bundle?.items_added;
      }
      case "bundle": {
        return !!bundle?.items_added;
      }
      case "with_variants": {
        return state?.product?.variants?.length > 1;
      }
      case "no_variants": {
        return state?.product?.variants?.length <= 1;
      }
    }
  };

  const hideIfEmpty = (element: HTMLElement, hide_if_empty: "none" | "section" | "container" | "block", isEmpty = false) => {
    switch (hide_if_empty) {
      case "none":
        break;
      case "section":
        element.closest(`.shopify-section`)?.classList.toggle("!hidden", isEmpty);
        break;
      case "container":
        element
          .closest(`[data-style-id]:not([data-style-id="${element.getAttribute("data-style-id")}"])`)
          ?.classList.toggle("!hidden", isEmpty);
        break;
      case "block":
        break;
    }
  };

  const getDataAttribute = (el) => {
    return el.hasAttribute("data-sold-out-action") && state.soldOut
      ? "sold-out-"
      : el.closest("[data-bundle-section]") && el.hasAttribute("data-bundle-action")
      ? "bundle-"
      : el.hasAttribute("data-pdp-action") && addon_target
      ? "pdp-"
      : el.hasAttribute("data-preorder-action") && state.preorder
      ? "preorder-"
      : el.hasAttribute("data-subscription-action") && state.hasSubscription
      ? "subscription-"
      : el.hasAttribute("data-variant-action") && state.hasVariants
      ? "variant-"
      : "";
  };

  const getCTAButtonProps = (el: HTMLElement, preview?: CardProductCardCtaButtonBlock["settings"]["theme_editor_preview"]) => {
    let attribute = getDataAttribute(el);

    if (preview) {
      switch (preview) {
        case "default":
          attribute = "";
          break;
        case "variant":
          attribute = "variant-";
          break;
        case "subscription":
          attribute = "";
          break;
        case "bundle":
          attribute = "bundle-";
          break;
        case "bundle_added":
          attribute = "bundle-";
          break;
        case "in_pdp":
          attribute = "pdp-";
          break;
        case "in_pdp_added":
          attribute = "pdp-";
          break;
        case "out_of_stock":
          attribute = "sold-out-";
          break;
      }
    }

    return {
      content: utils.richtextWithPrices(
        el.getAttribute(`data-${attribute}content`),
        state?.selected_variant?.price,
        state.selected_variant?.compare_at_price
      ),
      added_content: utils.richtextWithPrices(
        el.getAttribute(`data-${attribute}added-content`),
        state?.selected_variant?.price,
        state.selected_variant?.compare_at_price
      ),
      action: el.getAttribute(`data-${attribute}action`),
      classes: el.getAttribute(`data-${attribute}class`),
      product_modal_handle: el.getAttribute(`data-${attribute}product-modal-handle`),
    };
  };

  Alpine.effect(() => {
    const product = state.product;

    const selected_variant = state.selected_variant;

    state.hasVariants = product?.variants?.length > 1 || product?.variants?.[0]?.title !== "Default Title";
    state.hasSubscription = !!selected_variant?.options?.length;

    state.available_quantity =
      selected_variant?.inventory_management === "shopify" && selected_variant?.inventory_policy === "deny"
        ? selected_variant?.inventory_quantity -
          cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0)
        : selected_variant?.preorder && selected_variant?.preorder_quantity
        ? selected_variant?.inventory_quantity +
          selected_variant?.preorder_quantity -
          cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0)
        : 99999;

    state.soldOut = state.available_quantity <= 0;

    state.preorder =
      selected_variant?.preorder &&
      selected_variant?.inventory_quantity -
        cart?.state?.items?.reduce((acc, line) => (line.variant_id === selected_variant.id ? acc + line.quantity : acc), 0) <
        preorder_threshold;

    handleImageSelection(gallerySettings?.filter_images, selected_variant);
  });

  return {
    p_card: state,
    $el,
    $refs,
    handleAddToCart,
    handleBackInStockNotification,
    setSelectedVariant,
    setProductOption,
    setSellingPlan,
    getDynamicText,
    getDynamicValue,
    getDynamicValueWithFallbacks,
    getContentLabels,
    getDiscountLabel,
    getVariantSwatches,
    getOptionIndex,
    getOptionSwatches,
    truncateChildren,
    hasAvailableVariant,
    updateProductState,
    showConditionally,
    hideIfEmpty,
    getCTAButtonProps,
    handleImageSelection,
    scrollToMediaById,
  };
};

const fallbackHydrationQueue = new Set<HTMLElement>();

const hydrateProductCardContents = ($el: HTMLElement) => {
  const card_handle = $el.getAttribute(`data-card-handle`);
  const product_handle = $el.getAttribute(`data-product-handle`);
  const product_id = $el.getAttribute(`data-product-id`);
  const classes = utils.JSONParse<string[]>($el.getAttribute(`data-card-classes`) ?? "[]");
  const addon_target_product = $el.getAttribute("data-addon-product-target");
  const addon_auto_add = $el.hasAttribute("data-addon-auto-add");
  const addon_bundle_in_cart = $el.hasAttribute("data-addon-cart-bundle");

  const node = document.querySelector(`[data-product-card='${card_handle}']`)?.cloneNode(true) as HTMLElement | null;

  if (!node) {
    fallbackHydrationQueue.add($el);
    return;
  }

  node.removeAttribute("data-product-card");
  node.setAttribute("data-product-handle", product_handle ?? "");
  node.setAttribute("data-product-id", product_id ?? "");
  node.classList.add("card-loading");

  if (addon_target_product) {
    node.setAttribute("data-addon-product-target", addon_target_product);
    if (addon_auto_add) node.setAttribute("data-addon-auto-add", "");
    if (addon_bundle_in_cart) node.setAttribute("data-addon-cart-bundle", "");
  }

  node.querySelectorAll("[data-loop-item], [data-x-if], style").forEach((el) => {
    el.remove();
  });

  node.classList.add(...classes);
  $el.parentNode?.replaceChild(node, $el);
};

document.addEventListener("modalsLoaded", () => {
  fallbackHydrationQueue.forEach((el) => {
    hydrateProductCardContents(el);
  });
  fallbackHydrationQueue.clear();
});

const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.target instanceof HTMLElement) {
        hydrateProductCardContents(entry.target);
        observer.unobserve(entry.target);
      }
    }
  },
  { rootMargin: "300px 300px 300px 300px" }
);

const hydrateProductCard = ($el: HTMLElement) => {
  if ($el.hasAttribute("data-intersected")) {
    hydrateProductCardContents($el);
    return;
  }

  observer.observe($el);
};

export type InitProductCard = typeof initProductCard;
export type HydrateProductCard = typeof hydrateProductCard;

window._sections["initProductCard"] = initProductCard;
window._sections["hydrateProductCard"] = hydrateProductCard;
